import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import useResponsive from '@/shared/hooks/useResponsive';
import { TimeQuestionType } from '@/features/timeGame/types';

import AnalogClock from './AnalogClock';

import QuestionAnalogMobile from '@/features/timeGame/assets/images/question-analog-mobile.svg?react';
import QuestionAnalogDesktop from '@/features/timeGame/assets/images/question-analog.svg?react';
import QuestionDigitalMobile from '@/features/timeGame/assets/images/question-digital-mobile.svg?react';
import QuestionDigitalDesktop from '@/features/timeGame/assets/images/question-digital.svg?react';

import '@/features/timeGame/styles/question-block.scss';

interface QuestionBlockProps {
	questionType?: string;
	textContent?: string | any;
}

const QuestionBlock = ({ questionType = '', textContent = '' }: QuestionBlockProps) => {
	const { t } = useTranslation();
	const { isPortrait } = useResponsive();
	const displayType = useMemo(() => {
		if (questionType.includes(TimeQuestionType.DigitalToAnalog)) {
			return 'digital';
		}
		if (questionType.includes(TimeQuestionType.AnalogToDigital)) {
			return 'analog';
		}
		return 'text';
	}, [questionType]);

	const isDigitalType =
		questionType.includes('DigitalToAnalog') ||
		questionType.includes('TextToDigital') ||
		questionType.includes('TextToAnalog');

	// Parse hours and minutes from textContent for digital/analog types
	const { hours, minutes } = useMemo(() => {
		if (
			(displayType === 'digital' || displayType === 'analog') &&
			typeof textContent === 'string' &&
			/^\d{1,2}:\d{2}$/.test(textContent)
		) {
			const [h, m] = textContent.split(':').map(Number);
			return { hours: h, minutes: m };
		}
		return { hours: 0, minutes: 0 };
	}, [displayType, textContent]);

	const BackgroundSVG = useMemo(() => {
		if (isDigitalType) {
			return isPortrait ? QuestionDigitalMobile : QuestionDigitalDesktop;
		} else {
			return isPortrait ? QuestionAnalogMobile : QuestionAnalogDesktop;
		}
	}, [isDigitalType, isPortrait]);

	const renderContent = useMemo(() => {
		switch (displayType) {
			case 'digital':
				return (
					<div className="question-block__digital-time">
						<span className="question-block__time-hours">{String(hours).padStart(2, '0')}</span>
						<span className="question-block__time-separator">:</span>
						<span className="question-block__time-minutes">{String(minutes).padStart(2, '0')}</span>
					</div>
				);
			case 'analog':
				return (
					<div className="question-block__analog-wrapper">
						<AnalogClock hours={hours} minutes={minutes} />
					</div>
				);
			case 'text':
				return <div className="question-block__text" dangerouslySetInnerHTML={{ __html: textContent }} />;
			default:
				return null;
		}
	}, [displayType, hours, minutes, textContent]);

	return (
		<div className="question-block">
			<div
				className={classNames(
					'question-block__background',
					isDigitalType && 'question-block__background--digital',
				)}
			>
				<BackgroundSVG />
			</div>

			<div
				className={classNames(
					'question-block__content',
					isDigitalType ? 'question-block__content--digital' : 'question-block__content--analog',
				)}
			>
				<div className="question-block__label">{t('time_game.departure_time')}</div>

				<div className="question-block__display">{renderContent}</div>
			</div>
		</div>
	);
};

export default QuestionBlock;
